/**
 * Seller Hub — single dashboard that consolidates every seller action
 * live sale platforms-style: sidebar nav at left, content panel at right.
 *
 * Sections (all link to dedicated pages we already have):
 *   Overview      → today's GMV + orders + viewers snapshot
 *   Create        → /sell/new (fixed-price OR auction OR live-stream item)
 *   Schedule      → /dashboard (existing scheduled-streams form)
 *   Orders        → /orders (existing orders inbox)
 *   Payouts       → /payouts (existing payout statements)
 *
 * Gating:
 *   * Verified sellers → full hub.
 *   * Buyers (any flavour) → "Apply to sell" CTA → /apply.
 *   * Unauth → /login (handled at route level).
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  StorefrontIcon,
  PlusCircleIcon,
  CalendarBlankIcon,
  ReceiptIcon,
  BankIcon,
  ChartLineUpIcon,
  ArrowRightIcon,
  StarFourIcon,
} from "@phosphor-icons/react";

const SECTIONS = [
  { key: "overview", label: "Overview", path: "/seller-hub", icon: ChartLineUpIcon, exact: true },
  { key: "create",   label: "Create product", path: "/sell/new", icon: PlusCircleIcon, external: true },
  { key: "schedule", label: "Schedule shows", path: "/dashboard#schedule", icon: CalendarBlankIcon, external: true },
  { key: "orders",   label: "Orders", path: "/orders", icon: ReceiptIcon, external: true },
  { key: "payouts",  label: "Payouts", path: "/payouts", icon: BankIcon, external: true },
];

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
      <div className="text-3xl font-black text-zinc-900 mt-1 font-mono">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ActionTile({ to, icon: Icon, title, desc, testid, accent }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-2xl border border-zinc-200 bg-white p-5 hover:border-[#1D78AB] hover:shadow-lg hover:-translate-y-0.5 transition flex items-start gap-4"
    >
      <span className={`h-11 w-11 grid place-items-center rounded-xl ${accent || "bg-[#1D78AB] text-white"}`}>
        <Icon size={22} weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-black text-zinc-900 text-base">{title}</div>
        <div className="text-sm text-zinc-600 mt-0.5 leading-snug">{desc}</div>
      </div>
      <ArrowRightIcon size={18} className="text-zinc-400 group-hover:text-[#1D78AB] mt-1" />
    </Link>
  );
}

export default function SellerHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);

  // Buyers (non-seller account_type) get steered into the application flow.
  const isSeller = user && (user.account_type || "").includes("seller");
  const isApproved = user && user.verification_status === "approved";

  useEffect(() => {
    if (!isSeller) return;
    let alive = true;
    api.get("/me/seller-stats")
      .then(({ data }) => { if (alive) setStats(data); })
      .catch(() => { if (alive) setStats({}); });
    return () => { alive = false; };
  }, [isSeller]);

  if (!user) {
    // Route is auth-gated but be defensive.
    navigate("/login");
    return null;
  }

  if (!isSeller) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="seller-hub-apply-gate">
        <div className="rounded-3xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-white p-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-[11px] font-black uppercase tracking-wider">
            <StarFourIcon size={12} weight="fill" /> Bidorah Seller Hub
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-zinc-900 mt-3">Sell on Bidorah.</h1>
          <p className="text-zinc-600 mt-2 leading-relaxed">
            Run live auctions, scheduled shows, and fixed-price drops. Get paid out twice a week.
            Founder Seller slots come with 0% fees for the first 14 days (clock starts on approval) and a permanent badge.
          </p>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => navigate("/apply")}
              data-testid="seller-hub-apply-btn"
              className="rounded-full h-11 bg-[#1D78AB] hover:bg-[#1665a0] font-black"
            >
              Apply to sell
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/why-us")}
              className="rounded-full h-11 border-zinc-300"
            >
              Why Bidorah
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="seller-hub-page">
      <div className="flex items-start gap-3 mb-6">
        <span className="h-10 w-10 grid place-items-center rounded-xl bg-[#1D78AB] text-white">
          <StorefrontIcon size={22} weight="fill" />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">
            Seller Hub
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 leading-tight">
            Welcome back, {user.name || user.username}
          </h1>
          {!isApproved && (
            <div className="text-xs text-amber-700 mt-1">
              ⚠️ Verification pending — some seller actions are disabled until approval.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="rounded-2xl border border-zinc-200 bg-white p-2 h-fit hidden md:block">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = s.exact ? location.pathname === s.path : location.pathname.startsWith(s.path.split("#")[0]);
            return (
              <Link
                key={s.key}
                to={s.path}
                data-testid={`seller-hub-nav-${s.key}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold ${
                  active
                    ? "bg-[#1D78AB] text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {s.label}
              </Link>
            );
          })}
        </aside>

        {/* Content */}
        <div className="space-y-6">
          {/* Snapshot row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="GMV · 7d"
              value={stats?.gmv_7d_cents != null ? `$${(stats.gmv_7d_cents / 100).toFixed(0)}` : "—"}
              sub="Gross merchandise"
              testid="seller-hub-stat-gmv"
            />
            <StatCard
              label="Orders · 7d"
              value={stats?.orders_7d ?? "—"}
              sub={stats?.fulfilled_7d != null ? `${stats.fulfilled_7d} fulfilled` : ""}
              testid="seller-hub-stat-orders"
            />
            <StatCard
              label="Avg viewers"
              value={stats?.avg_viewers ?? "—"}
              sub="Last 7d streams"
              testid="seller-hub-stat-viewers"
            />
            <StatCard
              label="Payable"
              value={stats?.pending_payout_cents != null ? `$${(stats.pending_payout_cents / 100).toFixed(2)}` : "—"}
              sub="Cleared, awaiting next cycle"
              testid="seller-hub-stat-payout"
            />
          </div>

          {/* Big action tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ActionTile
              to="/sell/new"
              icon={PlusCircleIcon}
              title="Create product"
              desc="Fixed-price drop, auction, or attach to a live stream."
              testid="seller-hub-tile-create"
              accent="bg-[#1D78AB] text-white"
            />
            <ActionTile
              to="/dashboard#schedule"
              icon={CalendarBlankIcon}
              title="Schedule shows"
              desc="Pick a date and time. We'll handle reminders + early access for Plus."
              testid="seller-hub-tile-schedule"
              accent="bg-amber-400 text-zinc-900"
            />
            <ActionTile
              to="/orders"
              icon={ReceiptIcon}
              title="Orders"
              desc="Print labels, mark shipped, message buyers. SLA timer included."
              testid="seller-hub-tile-orders"
              accent="bg-emerald-500 text-white"
            />
            <ActionTile
              to="/payouts"
              icon={BankIcon}
              title="Payouts"
              desc="Bi-weekly direct deposits. Statements, holds, and Stripe Connect setup."
              testid="seller-hub-tile-payouts"
              accent="bg-purple-500 text-white"
            />
          </div>

          {/* Small marketing footer pushing them to Plus */}
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            🎬 <span className="font-bold">Want 4K Cinema mode on your streams?</span>{" "}
            Upgrade to{" "}
            <Link to="/plus" className="underline font-black">
              Bidorah Plus
            </Link>
            {" — $299/yr."}
          </div>
        </div>
      </div>
    </div>
  );
}
